import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Icon from "@/components/Icon";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import SiteImage from "@/components/SiteImage";

const Container = ({ children, className = "" }) => <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;

const PAGES = {
  "pour-les-medecins": {
    overline: "Pour les médecins", title: "Un secrétariat pensé pour les praticiens",
    intro: "Concentrez-vous sur vos patients. Nous prenons en charge la gestion administrative de votre activité, ponctuellement ou au quotidien.",
    img: "https://images.unsplash.com/photo-1758691461935-202e2ef6b69f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    points: ["Gestion des appels et des rendez-vous", "Traitement des e-mails administratifs", "Suivi de la facturation et des impayés", "Renfort ponctuel ou remplacement"],
  },
  "pour-les-cabinets": {
    overline: "Pour les cabinets", title: "Des solutions pour les cabinets médicaux",
    intro: "Du renfort ponctuel à la gestion complète de votre secrétariat, nous adaptons notre accompagnement à la taille et aux besoins de votre structure.",
    img: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    points: ["Remplacement de secrétariat sur plusieurs jours ou semaines", "Coordination administrative des opérations", "Organisation et suivi des rendez-vous", "Gestion documentaire administrative"],
  },
  "ouverture-reprise-cabinet": {
    overline: "Ouverture & reprise", title: "Ouverture ou reprise de cabinet médical",
    intro: "Un accompagnement administratif structuré pour chaque étape de votre projet d'ouverture ou de reprise de cabinet.",
    img: "https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    points: ["Préparation administrative de l'ouverture", "Accompagnement dans la reprise ou transmission", "Organisation des démarches et du suivi", "Coordination des interlocuteurs"],
  },
  "installation-suisse": {
    overline: "Installation en Suisse", title: "Installation en Suisse pour médecins",
    intro: "Vous souhaitez exercer ou vous installer professionnellement en Suisse ? Nous accompagnons vos démarches administratives.",
    img: "https://images.unsplash.com/photo-1606318313647-137d1f3b4d3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    points: ["Accompagnement administratif à l'installation", "Organisation des démarches par canton", "Suivi structuré de votre dossier", "Coordination des étapes clés"],
  },
  "office-manager": {
    overline: "Office Manager", title: "Office Manager externalisé",
    intro: "La coordination administrative de votre cabinet, sans recrutement interne ni charges fixes.",
    img: "https://images.unsplash.com/photo-1606318313647-137d1f3b4d3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
    points: ["Gestion administrative externalisée", "Coordination des opérations du cabinet", "Suivi des prestataires et fournisseurs", "Point de contact administratif unique"],
  },
};

// Public pages whose main image is configurable from Admin -> Site internet -> Images des pages.
const CONFIGURABLE_IMG = {
  "pour-les-medecins": "info_medecins",
  "pour-les-cabinets": "info_cabinets",
  "installation-suisse": "info_installation",
};

export default function InfoPage({ slug }) {
  const p = PAGES[slug] || PAGES["pour-les-medecins"];
  const navigate = useNavigate();
  const imgKey = CONFIGURABLE_IMG[slug];
  const [content, setContent] = useState(null);
  useEffect(() => {
    if (imgKey) api.get("/website/content").then(({ data }) => setContent(data || {})).catch(() => setContent({}));
  }, [imgKey]);
  const pos = content?.images_pos?.[imgKey];
  return (
    <div>
      <div className="bg-white border-b border-border py-16 md:py-20">
        <Container className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{p.overline}</span>
            <h1 className="mt-3 font-heading font-bold tracking-tight text-slate-900 text-4xl sm:text-5xl leading-[1.1]">{p.title}</h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{p.intro}</p>
            <ul className="mt-8 space-y-3">
              {p.points.map((pt, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />{pt}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-primary" onClick={() => navigate("/demande")} data-testid="infopage-request-btn">Faire une demande <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/contact")}>Nous contacter</Button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200">
            {imgKey ? (
              <SiteImage loading={content === null} mediaId={content?.images?.[imgKey]} fallback={p.img} alt={p.title}
                className="w-full h-[420px]"
                posX={pos?.x ?? 50} posY={pos?.y ?? 50} fit={pos?.fit || "cover"} zoom={pos?.zoom ?? 1} />
            ) : (
              <img src={p.img} alt={p.title} className="w-full h-[420px] object-cover" />
            )}
          </div>
        </Container>
      </div>
    </div>
  );
}
